import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import { Button, Table } from 'react-bootstrap';
import { RATINGS } from '../../../lib/schema.js';
import AuthorizedAction from '../../AuthorizedAction.js';
import {
  isDataExist,
  validateEditorData,
} from '../../editor/defaultOptions.js';
import NotesIcon from '../../icons/NotesIcon.jsx';
import RatingLabel from '../../RatingLabel.jsx';
import StakeholderProfileActionsDropdown from './StakeholderProfileActionsDropdown.jsx';
import styles from './StakeholdersTable.module.css';
import { StakeholderProfileLink } from '../../links/index.jsx';

export const STAKEHOLDER_ROW_ACTIONS = {
  // Edit / Download Report / Delete dropdown (project stakeholders page)
  MENU: 'menu',
  // single "View" button to the stakeholder profile page (read-only lists)
  VIEW_BUTTON: 'viewButton',
  NONE: 'none',
};

const StakeholdersTable = ({
  onHoverStakeholder,
  stakeholders,
  project,
  onShowCustomEditorField,
  isReportLoading,
  onDeleteStakeholder,
  onDownloadReportStakeholder,
  actionsVariant = STAKEHOLDER_ROW_ACTIONS.MENU,
}) => {
  const onMouseLeave = useCallback(() => {
    if (onHoverStakeholder) onHoverStakeholder(null);
  }, [onHoverStakeholder]);

  const showDefaultColumnNames = (name, nativeColumn) => {
    if (
      nativeColumn === 'influence' ||
      nativeColumn === 'interest' ||
      nativeColumn === 'impact'
    ) {
      if (nativeColumn === 'influence') {
        return nativeColumn === name ? 'Level of influence' : name;
      }
      if (nativeColumn === 'interest') {
        return nativeColumn === name ? 'Level of interest' : name;
      }
      if (nativeColumn === 'impact') {
        return nativeColumn === name ? 'Predicted impact' : name;
      }
    }
    return name;
  };

  const { stakeholderFields } = project;

  const sortedFields = stakeholderFields
    .filter((field) => field.tableShow)
    .sort((a, b) => a.priority - b.priority);
  const sortedFieldsKeys = sortedFields.map((field) => field.key);
  const editorFieldsKeys = sortedFields
    .filter((field) => field.editor)
    .map((field) => field.key);

  const renderTableCell = (key, stakeholder) => {
    const isEditorField = editorFieldsKeys.includes(key);
    const editorData = stakeholder[key] && validateEditorData(stakeholder[key]);
    const isEditorDataExist =
      stakeholder[key] && isDataExist(validateEditorData(stakeholder[key]));
    const { name, nativeColumn } = sortedFields.find(
      (field) => field.key === key
    );
    const fieldNumberOf = stakeholderFields.find(
      (field) => field.nativeColumn === 'member_count'
    ).key;

    if (fieldNumberOf === key && stakeholder[key] === null) {
      return <td key={key}>{0}</td>;
    }

    if (RATINGS.HIGH_MEDIUM_LOW.includes(stakeholder[key])) {
      return (
        <td key={key}>
          <RatingLabel
            rating={stakeholder[key]}
            reverse={nativeColumn === 'impact'}
          />
        </td>
      );
    }

    if (RATINGS.SUPPORT_LEVELS.includes(stakeholder[key])) {
      return (
        <td key={key}>
          <RatingLabel className="m-l-12" rating={stakeholder[key]} />
        </td>
      );
    }

    if (RATINGS.FREQUENCY_LEVELS.includes(stakeholder[key])) {
      return (
        <td key={key}>
          <RatingLabel className="m-l-12" rating={stakeholder[key]} />
        </td>
      );
    }

    if (!isEditorField && stakeholder[key] && stakeholder[key].length) {
      return (
        <td key={key}>
          {stakeholder[key].length <= 30 ? (
            stakeholder[key]
          ) : (
            <React.Fragment>
              {`${stakeholder[key].slice(0, 30)}...`}
              <br />
              {onShowCustomEditorField ? (
                <button
                  type="button"
                  className="btn-clear bg-transparent btn-link"
                  onClick={() => onShowCustomEditorField(name, editorData)}
                  style={{ paddingLeft: 0 }}
                >
                  Show more
                </button>
              ) : null}
            </React.Fragment>
          )}
        </td>
      );
    }

    if (
      isEditorField &&
      editorData &&
      isEditorDataExist &&
      onShowCustomEditorField
    ) {
      return (
        <td key={key}>
          <button
            type="button"
            className="btn-clear bg-transparent flex flex-center"
            onClick={() => onShowCustomEditorField(name, editorData)}
          >
            <NotesIcon />
          </button>
        </td>
      );
    }
    if (isEditorField) {
      return <td key={key}>—</td>;
    }

    return <td key={key}>{stakeholder[key] || '—'}</td>;
  };

  const renderHeaderCells = () =>
    sortedFields.map((field) => (
      <th key={field.id}>
        {showDefaultColumnNames(field.name, field.nativeColumn)}
      </th>
    ));

  return (
    <div
      className={styles.stakeholderTableWrapper}
      // min-height leaves room for the actions dropdown only
      style={
        actionsVariant === STAKEHOLDER_ROW_ACTIONS.MENU
          ? undefined
          : { minHeight: 0 }
      }
    >
      <Table hover className="scrollable-table">
        <thead>
          <tr>
            <th>Stakeholder or Group</th>
            {renderHeaderCells()}
            {actionsVariant === STAKEHOLDER_ROW_ACTIONS.MENU ? (
              <th className="text-right">Actions</th>
            ) : null}
            {actionsVariant === STAKEHOLDER_ROW_ACTIONS.VIEW_BUTTON ? (
              <th className="text-right">Action</th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {stakeholders.map((stakeholder, index) => {
            return (
              <tr
                key={stakeholder.id}
                onMouseEnter={
                  onHoverStakeholder
                    ? () => onHoverStakeholder(stakeholder.id)
                    : undefined
                }
                onMouseLeave={onMouseLeave}
              >
                <td className="cursor-pointer btn-link">
                  <StakeholderProfileLink
                    projectId={project.id}
                    stakeholderId={stakeholder.id}
                  >
                    <div className="flex flex-align-center">
                      <div className="profile-avatar small m-r-10">
                        <img src={stakeholder.avatar} alt="avatar" />
                      </div>
                      {stakeholder.name}
                    </div>
                  </StakeholderProfileLink>
                </td>
                {sortedFieldsKeys.map((key) =>
                  renderTableCell(key, stakeholder)
                )}
                {actionsVariant === STAKEHOLDER_ROW_ACTIONS.VIEW_BUTTON ? (
                  <td className="text-right">
                    <StakeholderProfileLink
                      projectId={project.id}
                      stakeholderId={stakeholder.id}
                    >
                      <Button bsStyle="primary">View</Button>
                    </StakeholderProfileLink>
                  </td>
                ) : null}
                {actionsVariant === STAKEHOLDER_ROW_ACTIONS.MENU ? (
                  <td style={{ zIndex: index }}>
                    <AuthorizedAction action="update" subject="stakeholder">
                      <StakeholderProfileActionsDropdown
                        projectId={project.id}
                        stakeholderId={stakeholder.id}
                        project={project}
                        onDelete={() => onDeleteStakeholder(stakeholder.id)}
                        onDownloadReport={() =>
                          onDownloadReportStakeholder(stakeholder)
                        }
                        isArchived={project.archived}
                        isReportLoading={isReportLoading}
                        actionsPosition="top"
                      />
                    </AuthorizedAction>
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};

StakeholdersTable.propTypes = {
  onHoverStakeholder: PropTypes.func,
  onShowCustomEditorField: PropTypes.func,
  actionsVariant: PropTypes.oneOf(Object.values(STAKEHOLDER_ROW_ACTIONS)),
  stakeholders: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      memberCount: PropTypes.number,
      influence: PropTypes.oneOf(RATINGS.HIGH_MEDIUM_LOW),
      interest: PropTypes.oneOf(RATINGS.HIGH_MEDIUM_LOW),
      impact: PropTypes.oneOf(RATINGS.HIGH_MEDIUM_LOW),
      notes: PropTypes.string,
    })
  ).isRequired,
};

export default StakeholdersTable;
